<template>
  <b-form @submit.prevent="submit" class="flex-center">
    <AuthWrapper
      :label="$lang.login"
      leftIcon="help-circle-outline"
      :leftIconTooltip="$lang.problem_login_link_caption"
      @handleLeftIcon="goToContactUs"
    >
      <template #main>
        <b-row>
          <b-col cols="12" md="12">
            <Input
              :title="$lang.enter_username_or_mobile"
              :label="$lang.username_or_mobile"
              :placeholder="$lang.username_or_mobile"
              :value="form.username"
              name="username"
              id="username"
              type="text"
              :minLength="lengthNumber5"
              :maxLength="lengthNumber16"
              :vuelidateObj="$v.form.username"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
          <b-col
            cols="12"
            md="12"
            class="flex-between otp font-size-12 font-weight-400 color-complementary-300 margin-bottom-8"
          >
            <span>{{ $lang.enter_password }}</span>
            <router-link
              :to="{ name: 'forget-password' }"
              tag="span"
              class="color-primary font-size-12 font-weight-500 cursor--pointer"
            >
              {{ $lang.forget_password }}
            </router-link>
          </b-col>
          <b-col cols="12" md="12">
            <Input
              :label="$lang.password"
              :placeholder="$lang.password"
              :value="form.password"
              name="password"
              id="password"
              type="password"
              :minLength="lengthNumber6"
              :maxLength="lengthNumber32"
              :vuelidateObj="$v.form.password"
              :hasLeftIcon="true"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
          <b-col cols="12" md="12">
            <Captcha
              :value="form.captcha"
              id="captcha"
              name="captcha"
              :minLength="captchaLength"
              :maxLength="captchaLength"
              :vuelidateObj="$v.form.captcha"
              @input="input"
              @getKey="updateCaptchaKey"
              @setShowCaptcha="setShowCaptcha"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
        </b-row>
      </template>
      <template #button>
        <b-row>
          <b-col
            v-if="isActiveSmsPanel()"
            cols="8"
            sm="7"
            md="6"
            class="padding-left-4"
          >
            <Button
              class="width-percent-100"
              contentClass="justify-content-center"
              variant="outline-primary"
              :text="$lang.login_with_code"
              @click="goToAuthMobile()"
            />
          </b-col>
          <b-col cols="4" sm="5" md="6" class="padding-right-4">
            <Button
              type="submit"
              class="width-percent-100"
              contentClass="justify-content-center"
              variant="primary"
              :text="$lang.login"
              :loading="loading || registrationCoursesLoading"
              @click="submit"
            />
          </b-col>
          <b-col
            cols="12"
            class="font-size-12 font-weight-400 text-center margin-top-16"
          >
            <span class="color-complementary-300 margin-left-8">
              {{ $lang.do_you_want_to_new_register }}
            </span>
            <router-link
              tag="span"
              class="color-primary cursor--pointer font-weight-500"
              :to="{ name: 'active-registrations' }"
            >
              {{ $lang.register }}
            </router-link>
          </b-col>
        </b-row>
      </template>
    </AuthWrapper>
  </b-form>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import { AuthWrapper } from "@/components/re-design";
import { Input, Captcha, Button } from "@/components/re-design/forms";
import {
  required,
  requiredIf,
  minLength,
  maxLength,
} from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import { pathOr } from "ramda";
import {
  isActiveSmsPanel,
  checkPersianCharValidation,
  goToRoute,
  storeDispatcher,
} from "@/shared/utils";
import { auth } from "@/modules/auth/services";

export default {
  inject: ["USERNAME", "goToVerifyCode", "goToContactUs"],
  mixins: [inputMixin],
  components: {
    AuthWrapper,
    Input,
    Captcha,
    Button,
  },
  validations() {
    return {
      form: {
        username: {
          required,
          persianCharacters: (val) => !checkPersianCharValidation(val),
          minLength: minLength(this.lengthNumber5),
          maxLength: maxLength(this.lengthNumber16),
        },
        password: {
          required,
          persianCharacters: (val) => !checkPersianCharValidation(val),
          minLength: minLength(this.lengthNumber6),
          maxLength: maxLength(this.lengthNumber32),
        },
        captcha: {
          required: requiredIf(() => this.showCaptcha),
          minLength: minLength(this.captchaLength),
          maxLength: maxLength(this.captchaLength),
        },
      },
    };
  },
  data() {
    return {
      form: {
        username: null,
        password: null,
        captcha: null,
        captchaKey: null,
      },
      loading: false,
    };
  },
  computed: {
    ...mapGetters(["registrationCoursesLoading"]),
  },
  methods: {
    pathOr,
    isActiveSmsPanel,
    checkPersianCharValidation,
    goToRoute,
    goToAuthMobile() {
      goToRoute("auth-mobile", {}, {}, false);
    },
    goToActiveRegistrations() {
      goToRoute("active-registrations", {}, {}, false);
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$anyError || this.loading) return;

      try {
        this.loading = true;
        const mode = "login";
        const username = this.form.username;
        const password = this.form.password;
        const captcha = this.form.captcha;
        const key = this.form.captchaKey;

        const response = await auth(mode, username, password, captcha, key);
        const { isTwoStep, mobile, resExpireSeconds, routeName, message } =
          response;

        if (!isTwoStep) {
          // successfully login
          this.$notify.success(message);
          return goToRoute(routeName, {}, {}, false);
        }

        // show verify code for login with two step form
        this.$emit("setMobile", mobile);
        this.$emit("setUsername", this.form.username);
        this.$emit("setPassword", this.form.password);
        this.$emit("setExpireSeconds", resExpireSeconds);
        this.goToVerifyCode();
      } catch (error) {
        this.$generateError(error);
        storeDispatcher("shouldRefreshCaptcha", true);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
